/**
 * The AI Ark glyph: a hull on the water with a cabin on it, for the sidebar
 * row, the rail, the palette and the page title.
 *
 * Drawn the way `components/icons.tsx` and `pages/discovery/icons.tsx` draw: a
 * 24x24 stroke path on the same grid, 1.75 stroke, round caps and joins,
 * decorative by default, so it sits level with the Discovery rows beside it.
 * Kept in the AI Ark directory so the shared icon files are not edited.
 */
import type { ReactNode } from 'react'
import { cn } from '../../../lib/cn'
import type { IconProps } from '../../../components/icons'

export function IconAiArk({ className, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cn('h-[1.15em] w-[1.15em] shrink-0', className)}
      {...props}
    >
      <path d="M3 13.5h18l-2.2 4.2a1.5 1.5 0 0 1-1.3.8h-11a1.5 1.5 0 0 1-1.3-.8z" />
      <path d="M8 13.5V9.5h8v4" />
      <path d="M7 9.5 12 6l5 3.5" />
      <path d="M3 21c1.5 0 1.5-1 3-1s1.5 1 3 1 1.5-1 3-1 1.5 1 3 1 1.5-1 3-1 1.5 1 3 1" />
    </svg>
  )
}

/** The filter groups' glyphs, on the same grid and stroke as IconAiArk. */
function Glyph({ className, children, ...props }: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cn('h-[1.15em] w-[1.15em] shrink-0', className)}
      {...props}
    >
      {children}
    </svg>
  )
}

/** Job title. */
export function IconBriefcase(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7M3 12.5h18" />
    </Glyph>
  )
}

/** Seniority: rising steps. */
export function IconSteps(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4 20h4v-5h4v-5h4V5h4" />
    </Glyph>
  )
}

/** A location. */
export function IconPin(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z" />
      <circle cx="12" cy="10" r="2.25" />
    </Glyph>
  )
}

/** A company. */
export function IconBuilding(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16M15 9h4a1 1 0 0 1 1 1v11M2.5 21h19" />
      <path d="M8 8h3M8 12h3M8 16h3" />
    </Glyph>
  )
}

/** Industry: a grid of sectors. */
export function IconGrid(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="4" y="4" width="6.5" height="6.5" rx="1" />
      <rect x="13.5" y="4" width="6.5" height="6.5" rx="1" />
      <rect x="4" y="13.5" width="6.5" height="6.5" rx="1" />
      <rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1" />
    </Glyph>
  )
}

/** Technologies: stacked layers. */
export function IconLayers(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="m12 4 8.5 4.5L12 13 3.5 8.5z" />
      <path d="m3.5 12.5 8.5 4.5 8.5-4.5M3.5 16.5 12 21l8.5-4.5" />
    </Glyph>
  )
}

/** Keywords. */
export function IconHash(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M5 9h15M4 15h15M10 4 8 20M16 4l-2 16" />
    </Glyph>
  )
}

/** Employee size: bars of growing height. */
export function IconBars(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M5 20v-5M10 20v-9M15 20V8M20 20V4" />
    </Glyph>
  )
}
